import 'focus-visible'
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import fallbackLogo from '../../../images/logo-social-media.jpg'

import Spacer from '../../_layout/Spacer'
import ArticleLayout from '../../_layout/Article/ArticleLayout'
import M046 from '../../organisms/M.M046/M046'

import ContentSwitch from './ContentSwitch'
import getDefaultStructuredData, { getPublisher } from '../../../utils/defaultStructuredData'
import handleTrackingScript from '../../../utils/handleTrackingScript'
import handleTrackingCookie from '../../../utils/handleTrackingCookie'
import {
  footerPropTypes,
  headerPropTypes,
  metaPropTypes,
  pageHeaderPropTypes,
  privacyPropTypes,
  clientListPropTypes,
} from '../../../utils/commonPropTypes'
import { loadCssHasPseudoLib } from '../../../utils/loadCssHasPseudoLib'

loadCssHasPseudoLib()

const Article = (props) => {
  const { pageContext, location } = props
  const isGatsby = typeof pageContext !== 'undefined'

  const siteUrl = isGatsby ? pageContext.siteUrl : props.siteUrl
  const trackingUrl = isGatsby ? pageContext.trackingUrl : props.trackingUrl
  const googleSiteVerification = isGatsby ? pageContext.googleSiteVerification : props.googleSiteVerification
  const meta = isGatsby ? pageContext.meta : props.meta
  const header = isGatsby ? pageContext.header : props.header
  const footer = isGatsby ? pageContext.footer : props.footer
  const privacy = isGatsby ? pageContext.privacy : props.privacy
  const readingTime = isGatsby ? pageContext.readingTime : props.readingTime
  const releaseDate = isGatsby ? pageContext.releaseDate : props.releaseDate
  const lastUpdated = isGatsby ? pageContext.lastUpdated : props.lastUpdated
  const pageHeader = isGatsby ? pageContext.pageHeader : props.pageHeader
  const content = isGatsby ? pageContext.content : props.content
  const tags = isGatsby ? pageContext.tags : props.tags
  const sources = isGatsby ? pageContext.sources : props.sources
  const author = isGatsby ? pageContext.author : props.author
  const y008 = isGatsby ? pageContext.Y008 : props.Y008
  const y013 = isGatsby ? pageContext.Y013 : props.Y013
  const y014 = isGatsby ? pageContext.Y014 : props.Y014
  const y015 = isGatsby ? pageContext.Y015 : props.Y015
  const y018 = isGatsby ? pageContext.Y018 : props.Y018
  const feedbackForm = isGatsby ? pageContext.feedbackForm : props.feedbackForm
  const settings = isGatsby ? pageContext.settings : props.settings
  const apis = isGatsby ? pageContext.apis : props.apis
  const surveyEnabled = isGatsby ? pageContext.surveyEnabled : props.surveyEnabled
  const clientList = isGatsby ? pageContext.clientList : props.clientList

  const [contentLoaded, setContentLoaded] = useState(false)

  const jumpLinks = content
    ?.filter((item) => item?.headline?.tag === 'h2' && item?.headline?.id && item?.headline?.text)
    .map((item) => {
      return {
        id: item.headline.id,
        title: item.headline.text,
      }
    })
  useEffect(() => {
    if (trackingUrl) {
      handleTrackingScript(trackingUrl)
    }

    handleTrackingCookie()
  }, [trackingUrl])

  const structuredData = [getDefaultStructuredData(siteUrl)]

  const metaData = Object.assign({}, meta, {
    location,
    siteUrl,
    structuredData,
    googleSiteVerification,
    image: !!meta?.image ? meta.image : { url: `${siteUrl}${fallbackLogo}`, alt: 'AOK Logo' },
  })

  const image = pageHeader?.image?.variants.length ? pageHeader?.image?.variants[0]?.url : undefined

  const structuredDataInformation = {
    image: image,
    headline: meta?.title,
    datePublished: releaseDate,
    dateModified: lastUpdated,
    keywords: meta?.keywords,
  }

  return (
    <ArticleLayout
      apis={apis}
      meta={metaData}
      feedbackForm={feedbackForm}
      header={header}
      footer={footer}
      privacy={privacy}
      clientList={clientList}
      y008={y008}
      y013={y013}
      y014={y014}
      y015={y015}
      y018={y018}
      surveyEnabled={surveyEnabled}
      structuredData={{
        ...structuredDataInformation,
        publisher: getPublisher(siteUrl),
        author: author ? [author] : [{ name: 'AOK - Die Gesundheitskasse' }], // apparently author.name is required in yggdrasil StructuredDataArticle
      }}
      sources={sources}
      tags={tags}
      readingTime={readingTime}
      releaseDate={releaseDate}
      lastUpdated={lastUpdated}
      author={author}
      pageHeader={pageHeader}
    >
      {settings && settings.showJumpLabels === true ? (
        <>
          <Spacer className="h-48 600:h-64 840:h-80" />
          <M046
            contentLoaded={contentLoaded}
            headline={{ text: 'Inhalte im Überblick', tag: 'p' }}
            jumpLinks={jumpLinks}
          />
          <Spacer className="h-48 600:h-64 840:h-80" />
        </>
      ) : (
        <Spacer className="h-48 600:h-64 840:h-80" />
      )}

      {content && (
        <ContentSwitch
          onLoadedComponents={() => setContentLoaded(true)}
          structuredDataInformation={structuredDataInformation}
          content={content}
        />
      )}
    </ArticleLayout>
  )
}

Article.propTypes = {
  readingTime: PropTypes.string,
  releaseDate: PropTypes.string,
  lastUpdated: PropTypes.string,
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string,
    })
  ),
  sources: PropTypes.string,
  author: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.shape({
      url: PropTypes.string,
      alt: PropTypes.string,
    }),
    profile: PropTypes.shape({
      url: PropTypes.string,
      title: PropTypes.string,
    }),
  }),
  settings: PropTypes.shape({
    showJumpLabels: PropTypes.bool,
  }),
  siteUrl: PropTypes.string,
  trackingUrl: PropTypes.string,
  googleSiteVerification: PropTypes.string,
  meta: PropTypes.shape(metaPropTypes),
  header: PropTypes.shape(headerPropTypes),
  pageHeader: PropTypes.shape(pageHeaderPropTypes),
  footer: PropTypes.shape(footerPropTypes),
  privacy: PropTypes.shape(privacyPropTypes),
  clientList: clientListPropTypes,
  content: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
    })
  ),
  pageType: PropTypes.string,
  apis: PropTypes.object,
  Y008: PropTypes.object,
  Y013: PropTypes.object,
  Y014: PropTypes.object,
  Y015: PropTypes.object,
  Y018: PropTypes.object,
  surveyEnabled: PropTypes.bool,
}

Article.defaultProps = {
  content: [],
  settings: {
    showJumpLabels: false,
  },
}

export default Article
